import { Link, type LinkProps } from 'react-router'
import { useLangParam, SUPPORTED_LANGS } from '@/shared/hooks'

/**
 * Props for LocalizedLink component.
 * Extends LinkProps but requires `to` to be a string.
 */
export interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: string
}

/**
 * Helper function to add language prefix to a path.
 * Exported for use in other components if needed.
 */
export function getLocalizedPath(path: string, lang: string): string {
  // Anchor links - no prefix
  if (path.startsWith('#')) {
    return path
  }

  // Relative paths (starting with . or ..) - no prefix
  if (path.startsWith('.')) {
    return path
  }

  // Absolute paths - add language prefix
  if (path.startsWith('/')) {
    // Check if already prefixed with any supported language
    const langPrefixRegex = new RegExp(`^/(${SUPPORTED_LANGS.join('|')})(/|$)`)
    if (langPrefixRegex.test(path)) {
      return path
    }
    return `/${lang}${path}`
  }

  // Other cases (relative paths without ./) - no prefix
  return path
}

/**
 * Link component that automatically adds language prefix to paths.
 *
 * Usage:
 * ```tsx
 * // Current language: fr
 *
 * // Absolute paths get prefixed
 * <LocalizedLink to="/dashboard">Dashboard</LocalizedLink>
 * // → <a href="/fr/dashboard">Dashboard</a>
 *
 * // Anchor links unchanged
 * <LocalizedLink to="#pricing">Pricing</LocalizedLink>
 * // → <a href="#pricing">Pricing</a>
 *
 * // Relative paths unchanged
 * <LocalizedLink to="../settings">Settings</LocalizedLink>
 * // → <a href="../settings">Settings</a>
 *
 * // Query params preserved
 * <LocalizedLink to="/settings?tab=billing">Billing</LocalizedLink>
 * // → <a href="/fr/settings?tab=billing">Billing</a>
 * ```
 *
 * All other Link props (className, children, etc.) are passed through.
 */
export function LocalizedLink({ to, ...props }: LocalizedLinkProps) {
  const { lang } = useLangParam()

  const localizedTo = getLocalizedPath(to, lang)

  return <Link to={localizedTo} {...props} />
}
